// The API is the authority on passwords (it mirrors the Auth0 connection's strength level and Auth0
// adds dictionary / personal-data checks). This is the same rule set evaluated as the person types,
// so the form can say what is still missing before anything is sent.
import { PasswordPolicy } from './authMode';

export interface PasswordRule {
  id: string;
  label: string;
  met: boolean;
}

const classesOf = (password: string) => ({
  lower: /[a-z]/.test(password),
  upper: /[A-Z]/.test(password),
  number: /[0-9]/.test(password),
  special: /[^A-Za-z0-9]/.test(password)
});

const CLASS_WORDS: Record<string, string> = {
  lower: 'a lowercase letter',
  upper: 'an uppercase letter',
  number: 'a number',
  special: 'a symbol'
};

/** The policy as a checklist for the current input. */
export function passwordRules(password: string, policy: PasswordPolicy): PasswordRule[] {
  const classes = classesOf(password);
  const present = Object.values(classes).filter(Boolean).length;
  const rules: PasswordRule[] = [
    {
      id: 'length',
      label: `At least ${policy.minLength} characters`,
      met: password.length >= policy.minLength && password.length <= policy.maxLength
    }
  ];
  if (policy.mandatoryCharacterClasses.length) {
    for (const name of policy.mandatoryCharacterClasses) {
      rules.push({
        id: name,
        label: `Contains ${CLASS_WORDS[name] ?? name}`,
        met: Boolean((classes as Record<string, boolean>)[name])
      });
    }
  } else if (policy.requiredCharacterClasses > 0) {
    rules.push({
      id: 'classes',
      label: `At least ${policy.requiredCharacterClasses} of: lowercase, uppercase, number, symbol`,
      met: present >= policy.requiredCharacterClasses
    });
  }
  if (policy.maxRepeatedCharacters > 0) {
    rules.push({
      id: 'repeat',
      label: `No character repeated more than ${policy.maxRepeatedCharacters} times in a row`,
      met: password.length > 0 && !new RegExp(`(.)\\1{${policy.maxRepeatedCharacters},}`).test(password)
    });
  }
  return rules;
}

export const passwordSatisfies = (password: string, policy: PasswordPolicy): boolean =>
  passwordRules(password, policy).every((rule) => rule.met);
